import { useMemo, useState } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { RecordingDetails, RecordingDetailsDialog } from './components/recording-details-dialog';
import { Permission } from '../../../../../../../../../backend/src/auth/permissions/permission';
import { useGetSubset } from '../../../../../../../api/queries/useGetSubset';
import { SubsetChip } from '../../../../../../components/subset-chip';
import { useMatterProcessingStore } from '../../../../../../stores/matter-processing-store';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateTranscript } from '@/api/mutations/useCreateTranscript';
import { useDeleteTranscript } from '@/api/mutations/useDeleteTranscript';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetAllTranscripts } from '@/api/queries/useGetTranscripts';
import { TranscriptWithCount } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/new/components/shadcn/table';
import { TablePagination } from '@/new/components/table/table-pagination';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { formatDatetimeForTable } from '@/new/lib/table-utils';
import { cn } from '@/new/lib/utils';

const fallbackTableData: TranscriptWithCount[] = [];
const columnHelper = createColumnHelper<TranscriptWithCount>();

function formatSeconds(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes ? minutes + 'm ' : ''}${remainingSeconds}s`;
}

function StatusChip({ status }: { status: string }) {
  let colour = 'bg-gray-400';

  switch (status) {
    case 'completed':
      colour = 'bg-green-400';
      break;

    case 'recording':
      colour = 'bg-red-400';
      break;

    default:
      break;
  }

  return (
    <div className="flex w-fit items-center gap-1.5 rounded-md border bg-gray-50 px-1.5 py-0.5 text-xs text-gray-800">
      <div className={cn(colour, 'size-1.5 rounded-full')}></div>
      {capitalizeFirstLetter(status)}
    </div>
  );
}

function ActionsCell({ transcript, caseId }: { transcript: TranscriptWithCount; caseId: string }) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { user } = useUserContext();
  const { mutate: deleteTranscript, isLoading: isDeleting } = useDeleteTranscript(caseId);

  const canDelete = user?.id === transcript.createdById;

  const handleDelete = () => {
    deleteTranscript(transcript.id, {
      onSuccess: () => {
        trackCustomEvent(TrackingEventsV2.RealtimeDeleteTranscript, { transcriptId: transcript.id });
        setDeleteModalOpen(false);
      },
      onError: () => {
        toast.error('Failed to delete transcript');
      },
    });
  };

  return (
    <>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          if (canDelete) setDeleteModalOpen(true);
        }}
        size={'sm'}
        variant={canDelete ? 'destructive' : 'default'}
        disabled={!canDelete}
      >
        Delete
      </Button>
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        title="Delete transcript"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      >
        <p>Are you sure you want to delete this transcript?</p>
      </ConfirmationModal>
    </>
  );
}

export function Realtime() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Realtime', path: 'realtime' },
  });

  document.title = 'Realtime • wexler.ai';

  const { i18n } = useTranslation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: () => <span>Transcript Name</span>,
        cell: (info) => (
          <Link
            to={info.row.original.id}
            onClick={(e) => e.stopPropagation()}
            className="line-clamp-1 min-w-48 break-all"
          >
            {info.getValue()}
          </Link>
        ),
      }),
      columnHelper.accessor('status', {
        header: () => <span>Status</span>,
        cell: (info) => <StatusChip status={info.getValue()} />,
      }),
      columnHelper.accessor('durationSeconds', {
        header: () => <span>Duration</span>,
        cell: (info) => {
          const seconds = info.getValue();
          return <span className="whitespace-nowrap">{seconds ? formatSeconds(seconds) : '-'}</span>;
        },
      }),
      columnHelper.accessor('inconsistencyCount', {
        header: () => <span>Inconsistencies</span>,
        cell: (info) => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor('createdDate', {
        header: () => <span>Date Created</span>,
        cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span>Actions</span>,
        cell: (info) => <ActionsCell transcript={info.row.original} caseId={subsetId} />,
      }),
    ],
    [i18n, subsetId],
  );

  const { data: transcripts, isLoading } = useGetAllTranscripts(subsetId);

  const navigate = useNavigate();

  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: transcripts ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      globalFilter,
    },
  });

  const [showRecordingDetailsModal, setShowNewRecordingModal] = useState(false);

  const handleNewRecordingClick = () => {
    trackCustomEvent(TrackingEventsV2.RealtimeClickNewRecording);
    setShowNewRecordingModal(true);
  };

  const createTranscriptMutation = useCreateTranscript();

  const createNewTranscript = async (details: RecordingDetails) => {
    const newTranscript = await createTranscriptMutation.mutateAsync({
      caseId: subsetId,
      name: details.name,
      objective: details.objective,
      deponent: details.deponent,
      otherSpeakers: details.otherSpeakers,
      phraseList: details.phraseList,
    });

    setShowNewRecordingModal(false);

    navigate(newTranscript.id);
  };

  const { getProcessingState } = useMatterProcessingStore();

  return (
    <>
      <Breadcrumb items={breadcrumbItems} />

      <PageContainer>
        <PageHeader>
          <div className="flex items-center gap-4">
            <PageTitle text="Realtime" />
            <SubsetChip subsetId={subsetId} className="mt-0.5" />
          </div>
        </PageHeader>

        <div className="my-6 flex items-center gap-2">
          <SearchInput
            placeholder="Search transcripts..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="max-w-sm grow"
          />

          <PermissionGate permission={Permission.RealtimeUse} uiMode="disabled">
            <Button
              variant="primary"
              onClick={handleNewRecordingClick}
              disabled={!getProcessingState(matterId)?.hasCompletedRuns}
            >
              <Icon className="lucide--circle-fading-plus" />
              Start a New Recording
            </Button>
          </PermissionGate>
        </div>

        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} onClick={() => navigate(row.original.id)} className="cursor-pointer">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {!isLoading && <TablePagination table={table} plural="transcripts" />}
      </PageContainer>

      <RecordingDetailsDialog
        isOpen={showRecordingDetailsModal}
        onClose={() => setShowNewRecordingModal(false)}
        onConfirm={createNewTranscript}
        isLoading={createTranscriptMutation.isLoading}
      />
    </>
  );
}
